import React, { createContext, useContext, useState, useEffect } from 'react';
import { useDevMode } from './DevModeContext';
import { InkQuoteApi } from '../services/inkquoteApi';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const { isDevMode } = useDevMode();
  
  // Load favorites from localStorage, file, and detect existing favorites from metadata
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const favoriteIds = new Set();
        
        // First, load from localStorage (most recent user changes)
        try {
          const localFavorites = localStorage.getItem('userFavorites');
          if (localFavorites) {
            const localIds = JSON.parse(localFavorites);
            localIds.forEach(id => favoriteIds.add(id));
            if (process.env.REACT_APP_MODE === 'development') {
              console.log('Loaded from localStorage:', localIds.length, 'favorites');
            }
          }
        } catch (localError) {
          console.warn('Could not load from localStorage:', localError);
        }
        
        // Also load existing favorites from roam quotes and tweets metadata
        const sources = [
          '/exports/roam/roam_quotes.json',
          '/exports/likes/merged_tweets.json'
        ];
        
        for (const source of sources) {
          try {
            const sourceResponse = await fetch(source);
            if (sourceResponse.ok) {
              const sourceData = await sourceResponse.json();
              const items = Array.isArray(sourceData) ? sourceData : sourceData.data || [];
              
              items.forEach(item => {
                // Check if item has favorite tag in metadata
                const tags = item.metadata?.tags || item._raw_metadata?.tags || [];
                if (tags.includes('favorite')) {
                  favoriteIds.add(item.id);
                }
              });
            }
          } catch (sourceError) {
            console.warn(`Could not check favorites in ${source}:`, sourceError);
          }
        }
        
        // Load from overrides file as backup/additional source
        try {
          const response = await fetch('/exports/favorites_overrides.json');
          if (response.ok) {
            const data = await response.json();
            data.data.forEach(item => favoriteIds.add(item.id));
          }
        } catch (fileError) {
          console.warn('Could not load from overrides file:', fileError);
        }
        
        // console.log('Total loaded favorites:', Array.from(favoriteIds));
        setFavorites(favoriteIds);
      } catch (error) {
        console.warn('Could not load favorites:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFavorites();
  }, []);

  // Save favorites to localStorage and file (dev mode only)
  const saveFavorites = async (updatedFavorites) => {
    const favoritesArray = Array.from(updatedFavorites);
    
    // Always save to localStorage for immediate UI responsiveness
    localStorage.setItem('userFavorites', JSON.stringify(favoritesArray));
    if (process.env.REACT_APP_MODE === 'development') {
      console.log('Saved to localStorage:', favoritesArray.length, 'favorites');
    }

    // In dev mode, also save to file for permanent storage
    if (isDevMode && process.env.REACT_APP_MODE === 'development') {
      try {
        const data = {
          version: 1.0,
          data: favoritesArray.map(id => ({ id }))
        };

        // Use a timeout to debounce file writes and prevent reload triggers
        clearTimeout(window.favoritesSaveTimeout);
        window.favoritesSaveTimeout = setTimeout(async () => {
          try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            await fetch(`${apiUrl}/api/save-favorites`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(data),
            });
            if (process.env.REACT_APP_MODE === 'development') {
              console.log('Saved to file successfully');
            }
          } catch (error) {
            console.warn('Background save to file failed:', error);
          }
        }, 1000); // Wait 1 second before saving to file
      } catch (error) {
        console.warn('Error preparing file save:', error);
      }
    }
  };

  const toggleFavorite = async (itemId) => {
    if (process.env.REACT_APP_MODE === 'development') {
      console.log('toggleFavorite called for:', itemId);
    }

    // Allow toggling if:
    // 1. In dev mode (for local development with file saves), OR
    // 2. API is configured (production with credentials)
    const canToggle = isDevMode || InkQuoteApi.isConfigured();

    if (!canToggle) {
      if (process.env.REACT_APP_MODE === 'development') {
        console.log('Cannot toggle: not in dev mode and API not configured');
      }
      return;
    }

    try {
      let wasFavorite;
      let updated;

      // Optimistic update
      setFavorites(prev => {
        if (process.env.REACT_APP_MODE === 'development') {
          console.log('Current favorites:', Array.from(prev));
        }
        updated = new Set(prev);
        wasFavorite = updated.has(itemId);

        if (wasFavorite) {
          updated.delete(itemId);
          if (process.env.REACT_APP_MODE === 'development') {
            console.log('Removed favorite:', itemId);
          }
        } else {
          updated.add(itemId);
          if (process.env.REACT_APP_MODE === 'development') {
            console.log('Added favorite:', itemId);
          }
        }

        if (process.env.REACT_APP_MODE === 'development') {
          console.log('Updated favorites:', Array.from(updated));
        }

        // Save immediately (localStorage is synchronous, file save is async)
        saveFavorites(updated);

        return updated;
      });

      // Save to API if configured
      if (InkQuoteApi.isConfigured()) {
        try {
          const result = await InkQuoteApi.toggleFavorite(itemId, !wasFavorite);
          if (process.env.REACT_APP_MODE === 'development') {
            console.log('Successfully synced favorite to API:', itemId, result);
          }
          if (result.localOnly) {
            console.log('Item not in API, updated locally only:', itemId);
          }
        } catch (error) {
          console.error('Failed to sync favorite to API:', error);
          // Don't revert - keep local change even if API fails
        }
      }
    } catch (error) {
      console.error('Error in toggleFavorite:', error);
    }
  };

  const isFavorite = (itemId) => {
    return favorites.has(itemId);
  };

  // Check if user can toggle favorites (dev mode OR API configured)
  const canToggleFavorites = isDevMode || InkQuoteApi.isConfigured();

  const value = {
    favorites,
    isFavorite,
    toggleFavorite,
    canToggleFavorites,
    isLoading
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};